import { CmpEventRegistrationFieldsFragment } from "../../../../graphql/types";
import { getActiveEventAttendees } from "./eventSalesAttendeeUpdates";

// One registration (ticket invoice) can hold several ticket types, and one
// purchased ticket can include several attendees (for example a table of 8).
// These helpers give Manage Ticket Sales, the Update Attendee Details modal
// and the Create Ticket Invoice page one shared grouping rule.

export type EventTicketTypeNameSource = Readonly<{
  attendeesPerTicket?: number | null;
  id: string;
  name: string;
}>;

/** One ticket type on a registration and how many tickets of it were bought. */
export type EventTicketTypeLine = Readonly<{
  label: string;
  quantity: number;
  ticketTypeId: string;
}>;

/** Header data for one ticket type group in the attendee editor. */
export type EventAttendeeTicketGroupHeader = Readonly<{
  attendeesPerTicket: number;
  label: string;
  ticketCount: number;
  ticketTypeId: string;
}>;

export type EventAttendeeTicketUnit<T> = Readonly<{
  attendees: T[];
  /** 1-based position of the first attendee of this ticket inside its group. */
  firstPosition: number;
  /** "Ticket u of n", or null when the group needs no per-ticket split. */
  label: string | null;
}>;

export type EventAttendeeTicketGroup<T> = EventAttendeeTicketGroupHeader &
  Readonly<{ attendees: T[]; units: EventAttendeeTicketUnit<T>[] }>;

type RegistrationTicketSource = Pick<
  CmpEventRegistrationFieldsFragment,
  "attendees" | "ticketQuantities" | "ticketQuantity"
>;

const normalizeSeats = (value?: number | null) =>
  Math.max(1, Math.floor(Number(value) || 1));

const getTicketTypeSeats = (
  ticketTypes: readonly EventTicketTypeNameSource[],
  ticketTypeId: string,
) =>
  normalizeSeats(
    ticketTypes.find((ticketType) => ticketType.id === ticketTypeId)
      ?.attendeesPerTicket,
  );

/** Distinct values in first-appearance order. */
const uniqueInOrder = (values: readonly string[]) =>
  values.filter((value, index) => values.indexOf(value) === index);

/**
 * Ticket type lines for a registration: one entry per ticket type with the
 * purchased quantity. The backend ticketQuantities list is authoritative and
 * is already in first-appearance order. Legacy registrations without it fall
 * back to the active attendees: one type uses the registration ticket count;
 * several types derive each count from the attendees and seats per ticket.
 * Labels use the current event ticket name, then the attendee name snapshot.
 */
export const getEventRegistrationTicketTypeLines = ({
  registration,
  ticketTypes,
}: {
  registration: RegistrationTicketSource;
  ticketTypes: readonly EventTicketTypeNameSource[];
}): EventTicketTypeLine[] => {
  const labelFor = (ticketTypeId: string) =>
    ticketTypes.find((ticketType) => ticketType.id === ticketTypeId)?.name ||
    registration.attendees.find(
      (attendee) => attendee.ticketTypeId === ticketTypeId,
    )?.ticketTypeNameSnapshot ||
    "Ticket";

  const quantities = (registration.ticketQuantities || []).filter(
    (entry) => entry.quantity > 0,
  );
  if (quantities.length > 0) {
    return quantities.map((entry) => ({
      label: labelFor(entry.ticketTypeId),
      quantity: entry.quantity,
      ticketTypeId: entry.ticketTypeId,
    }));
  }

  const activeAttendees = getActiveEventAttendees(registration.attendees);
  const ticketTypeIds = uniqueInOrder(
    activeAttendees.map((attendee) => attendee.ticketTypeId),
  );
  return ticketTypeIds.map((ticketTypeId) => {
    const attendeeCount = activeAttendees.filter(
      (attendee) => attendee.ticketTypeId === ticketTypeId,
    ).length;
    return {
      label: labelFor(ticketTypeId),
      quantity:
        ticketTypeIds.length === 1 && registration.ticketQuantity != null
          ? registration.ticketQuantity
          : Math.ceil(
              attendeeCount / getTicketTypeSeats(ticketTypes, ticketTypeId),
            ),
      ticketTypeId,
    };
  });
};

/** Header data for the attendee editor groups of one registration. */
export const getEventAttendeeTicketGroupHeaders = ({
  lines,
  ticketTypes,
}: {
  lines: readonly EventTicketTypeLine[];
  ticketTypes: readonly EventTicketTypeNameSource[];
}): EventAttendeeTicketGroupHeader[] =>
  lines.map((line) => ({
    attendeesPerTicket: getTicketTypeSeats(ticketTypes, line.ticketTypeId),
    label: line.label,
    ticketCount: line.quantity,
    ticketTypeId: line.ticketTypeId,
  }));

/**
 * Seats in a ticket type group: purchased tickets x attendees per ticket, so
 * one Table of 8 counts as 8 (design D:10203).
 */
export const getEventAttendeeTicketGroupSeatCount = ({
  attendeesPerTicket,
  ticketCount,
}: Pick<EventAttendeeTicketGroupHeader, "attendeesPerTicket" | "ticketCount">) =>
  Math.max(0, Math.floor(ticketCount) || 0) * normalizeSeats(attendeesPerTicket);

/**
 * Splits one group's attendees into purchased tickets. The split shows only
 * when a ticket includes more than one attendee and more than one ticket was
 * bought, matching the website "Label — Ticket u of n" rule.
 */
export const splitEventAttendeesByTicket = <T>({
  attendees,
  attendeesPerTicket,
  ticketCount,
}: {
  attendees: readonly T[];
  attendeesPerTicket: number;
  ticketCount: number;
}): EventAttendeeTicketUnit<T>[] => {
  const seats = normalizeSeats(attendeesPerTicket);
  const unitCount = Math.ceil(attendees.length / seats);
  // Deleted complimentary attendees can leave fewer rows than seats; keep
  // "of n" equal to the purchased ticket count in that case.
  const totalTickets = Math.max(unitCount, Math.floor(ticketCount) || 0);
  if (seats <= 1 || totalTickets <= 1 || unitCount <= 1) {
    return [{ attendees: [...attendees], firstPosition: 1, label: null }];
  }
  return Array.from({ length: unitCount }, (_, unitIndex) => ({
    attendees: attendees.slice(unitIndex * seats, (unitIndex + 1) * seats),
    firstPosition: unitIndex * seats + 1,
    label: `Ticket ${unitIndex + 1} of ${totalTickets}`,
  }));
};

/**
 * Orders attendees into ticket type groups. Groups follow the header order
 * (the registration ticket order); attendees keep their saved order inside a
 * group. An attendee whose ticket type has no header gets its own group at
 * the end so that no attendee is hidden. Empty groups are left out.
 */
export const groupEventAttendeesByTicketType = <
  T extends { ticketTypeId?: string | null },
>({
  attendees,
  headers,
  fallbackLabel = "Ticket",
}: {
  attendees: readonly T[];
  headers: readonly EventAttendeeTicketGroupHeader[];
  fallbackLabel?: string;
}): EventAttendeeTicketGroup<T>[] => {
  const knownIds = new Set(headers.map((header) => header.ticketTypeId));
  const extraHeaders: EventAttendeeTicketGroupHeader[] = uniqueInOrder(
    attendees
      .map((attendee) => attendee.ticketTypeId || "")
      .filter((ticketTypeId) => !knownIds.has(ticketTypeId)),
  ).map((ticketTypeId) => ({
    attendeesPerTicket: 1,
    label: fallbackLabel,
    ticketCount: attendees.filter(
      (attendee) => (attendee.ticketTypeId || "") === ticketTypeId,
    ).length,
    ticketTypeId,
  }));

  return [...headers, ...extraHeaders]
    .map((header) => {
      const groupAttendees = attendees.filter(
        (attendee) => (attendee.ticketTypeId || "") === header.ticketTypeId,
      );
      return {
        ...header,
        attendees: groupAttendees,
        units: splitEventAttendeesByTicket({
          attendees: groupAttendees,
          attendeesPerTicket: header.attendeesPerTicket,
          ticketCount: header.ticketCount,
        }),
      };
    })
    .filter((group) => group.attendees.length > 0);
};

/** "Member Ticket x2; Table of 8 x1" (design D:10102). */
export const formatEventTicketTypeLinesForCsv = (
  lines: ReadonlyArray<Readonly<{ label: string; quantity: number | null }>>,
): string =>
  lines
    .map((line) =>
      line.quantity == null ? line.label : `${line.label} x${line.quantity}`,
    )
    .join("; ");

/**
 * "Gold Sponsorship Includes 2 Tickets" (design D:1823552). The design drops
 * a trailing "Sponsor" or "Sponsorship" from the level name first.
 */
export const getSponsorshipIncludedTicketsHeading = (
  levelName: string,
  ticketCount: number,
): string => {
  const level = levelName.trim().replace(/\s*Sponsor(ship)?$/i, "");
  const tickets = ticketCount === 1 ? "Ticket" : "Tickets";
  return `${level ? `${level} ` : ""}Sponsorship Includes ${ticketCount} ${tickets}`;
};
